import { ApplicationV1Enum } from "@/api-schema-enums";
import { useApplicationByUuidQuery } from "@/common/api/application/use-application-by-uuid.query";
import { useApplicationUpdateMutation } from "@/common/api/application/use-application-update.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Form } from "@/components/ui/form";
import { FormCard, FormCardActions } from "@/components/ui/form-card";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { ApplicationFormFields, formSchema, formSchemaType } from "./_components/application-form-fields";

export function ApplicationUpdateView() {
  const { t } = useTranslation(["ram", "common"]);
  const pageTitle = t("ram:EditApp");
  const pageSubtitle = t("common:ReviewAndSaveChangesDescription");
  const [_, navigate] = useLocation();
  const params = useParams<{ applicationUuid: string }>();
  const applicationByUuidQueryInstance = useApplicationByUuidQuery(params.applicationUuid);
  const { data: application } = applicationByUuidQueryInstance;
  const { isPending, mutateAsync } = useApplicationUpdateMutation();
  const form = useForm<formSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      azureClientUuid: "",
      isV1Application: ApplicationV1Enum.IsNotV1,
    },
  });

  useEffect(() => {
    if (application) {
      form.setValue("azureClientUuid", application?.azureClientUuid);
      form.setValue("isV1Application", application?.isV1Application);
    }
  }, [application, form]);

  const onSubmit = async (values: formSchemaType) => {
    await toastMutationStatus({
      mutationFn: () => mutateAsync({ ...values, uuid: params.applicationUuid }),
      successMessage: t("ram:AppHasBeenUpdated"),
      errorMessage: t("ram:ErrorInUpdatingTheApp"),
    });

    navigate("/");
  };

  return (
    <UuidExistenceWrapper
      uuid={params.applicationUuid}
      query={useApplicationByUuidQuery}
      queryResult={applicationByUuidQueryInstance}
      notFoundProps={{
        backTo: "~/ram/application",
        backToLabel: t("ram:Apps"),
      }}
    >
      <CenteredLayout variant={CenteredLayoutVariant.Form} title={pageTitle} subTitle={pageSubtitle}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormCard title={pageTitle} titleHidden>
              <ApplicationFormFields formControl={form.control} />
            </FormCard>

            <FormCardActions
              cancelLabel={t("common:Cancel")}
              onCancel={() => navigate("/")}
              isPending={isPending}
              submitLabel={t("common:Save")}
            />
          </form>
        </Form>
      </CenteredLayout>
    </UuidExistenceWrapper>
  );
}
